import React from 'react';
import SearchableLoadMoreList, {
  RowItemProps,
} from 'components/profile/SearchableLoadMoreList';
import {
  EmailPreference,
  MerchantEmailPreference,
  Product,
  Value,
} from 'routes/profile/models/EmailPreference';
import { useUpdateEmailPreferenceMutation } from 'store/query/api-extensions/receiptAndMarketingPreferences';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { openToast, ToastVariant } from 'store/toastSlice';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';
import {
  actionSearchMerchants,
  actionToggleMarketing,
} from 'services/tracking/cdp/events/marketingPreferences';

type MerchantMarketingPrefsProps = {
  emailId: string;
  merchantPrefs: MerchantEmailPreference[];
  isLoading: boolean;
  onRowToggle?: (isEnabled: boolean, merchantId: string) => Promise<void>;
};

const MerchantMarketingPrefs: React.FC<MerchantMarketingPrefsProps> = ({
  emailId,
  merchantPrefs,
  isLoading,
  onRowToggle: externalRowToggle,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [updateEmailPreference] = useUpdateEmailPreferenceMutation();

  const data: RowItemProps[] = merchantPrefs.map((mPref) => {
    const { imageUrl, displayName, id } = mPref.merchantInfo;
    return {
      displayName,
      enabled: mPref.marketingPreference.value === Value.OptIn,
      id,
      imageUrl,
    };
  });

  return (
    <SearchableLoadMoreList
      ariaLabel={t(
        'profile.marketingPreferences.merchantPreferences.ariaLabel'
      )}
      ariaLabelItemFunc={(businessName: string) =>
        t('profile.marketingPreferences.merchantPreference.ariaLabel', {
          businessName,
        })
      }
      isLoading={isLoading}
      rowModels={data}
      onSearch={() => {
        buyerportalCdpClient.track(
          'Search Merchants',
          actionSearchMerchants(emailId)
        );
      }}
      onRowToggle={
        externalRowToggle ??
        ((isEnabled: boolean, id: string) =>
          updateEmailPreference({
            emailId,
            merchantId: id,
            preference: new EmailPreference({
              product: Product.Marketing,
              value: isEnabled ? Value.OptIn : Value.OptOut,
            }),
          })
            .unwrap()
            .then(() => {
              buyerportalCdpClient.track(
                'Toggle Marketing',
                actionToggleMarketing(emailId, isEnabled, id)
              );
            })
            .catch(() => {
              dispatch(
                openToast({
                  message: t('common.somethingWentWrong.retryable.direct'),
                  variant: ToastVariant.Critical,
                })
              );
            }))
      }
    />
  );
};

export default MerchantMarketingPrefs;
